import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { ReactComponent as EyeIcon } from 'assets/icons/eye.svg';
import Button from 'components/Button/Button';
import DeactivationModal from 'components/DeactivationModals/DeactivationModal';
import EmptyList from 'components/EmptyList/EmptyList';
import IconButton from 'components/IconButton/IconButton';
import Loader from 'components/Loader/Loader';
import Panel from 'components/Panel/Panel';
import StatusTaskButton from 'components/StatusTaskButton/StatusTaskButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import TableSort from 'components/Table/TableSort';
import AppRoutes from 'constants/AppRoutes';
import { TASK_HEAD_CELLS } from 'constants/dataSets';
import { SORT } from 'constants/formFields';
import { TASK_SORT_BY, TASK_TYPE, TYPE_RELATE_TO } from 'constants/general';
import { BUTTON_VARIANT, type TTaskStatus } from 'constants/interfaces';
import { TASK_PRIORITY_OPTIONS, TASK_STATUS_REQUIRED_COMMENT } from 'constants/taskPriority';
import { useUpdateStatusTaskMutation } from 'store/slices/tasksManagement/apis/tasksManagementApi';

import type ITask from 'store/slices/tasksManagement/interfaces/ITask';
import type ITaskListBoard from '../interfaces/ITaskListBoard';

import styles from '../sass/TaskListBoard.module.scss';

function TaskListBoard({
  taskList, control, onReadMoreClients,
  hasNext, onSortChange, isLoading,
  canChangeStatus,
}: ITaskListBoard) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentTaskList, setCurrentTaskList] = useState<ITask[]>(taskList);
  const [isCommentModalVisible, setIsCommentModalVisible] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    taskId: number,
    newStatus: TTaskStatus,
  } | null>(null);

  const [updateStatusTask] = useUpdateStatusTaskMutation();

  const emptyListText = (
    <Trans
      i18nKey="common.noResultsWithFilters"
      t={t}
      components={{
        br: <br />,
      }}
    />
  );

  const onCheckExistStatus = (status: TTaskStatus) => TASK_STATUS_REQUIRED_COMMENT.includes(status);

  useEffect(() => {
    setCurrentTaskList(taskList);
  }, [taskList]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const threshold = 300;
      const isNearBottom = scrollTop + windowHeight >= documentHeight - threshold;
      if (isNearBottom && hasNext && !isLoading && onReadMoreClients) {
        onReadMoreClients();
      }
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasNext, isLoading, onReadMoreClients]);

  const onGoToTask = (id: string) => {
    navigate(generatePath(AppRoutes.taskManagementId, { taskId: String(id) }));
  };

  const onCommentConfirm = (comment: string) => {
    if (pendingStatusChange) {
      const updatedTaskList = currentTaskList.map((task) => (task.id === pendingStatusChange.taskId
        ? { ...task, status: pendingStatusChange.newStatus }
        : task));
      setCurrentTaskList(updatedTaskList);
      setPendingStatusChange(null);
      const findTask = currentTaskList.find((task) => task.id === pendingStatusChange.taskId);
      if (findTask) {
        updateStatusTask({
          id: pendingStatusChange.taskId,
          status: pendingStatusChange.newStatus,
          updateComment: comment,
          type: findTask.type,
          materialityAssessmentId: findTask.materialityAssessment?.id,
          valueChainMapId: findTask.valueChainMap?.id,
        });
      }
    }
    setIsCommentModalVisible(false);
  };

  const onCommentReject = () => {
    setPendingStatusChange(null);
    setIsCommentModalVisible(false);
  };

  const onStatusChange = (taskId: number, newStatus: TTaskStatus) => {
    setPendingStatusChange({ taskId, newStatus });
    setIsCommentModalVisible(true);
  };

  const handleGetCurrentRelatedTo = (task: ITask) => {
    let relatedTo = '';
    if (task.type === TASK_TYPE.VCM) {
      relatedTo = task.valueChainMap ? task.valueChainMap.name : TYPE_RELATE_TO.VALUE_CHAIN;
    } else if (task.type === TASK_TYPE.MA) {
      relatedTo = task.materialityAssessment ? task.materialityAssessment.name : TYPE_RELATE_TO.MATERIALITY_ASSESSMENT;
    }

    return relatedTo;
  };
  return (
    <div className={styles.root}>
      <Panel className={styles.panel}>
        {taskList.length > 0 ? (
          <Table className={styles.table}>
            <TableHead className={styles.header}>
              {TASK_HEAD_CELLS.map(({
                id, label, withSort,
              }) => (withSort ? (
                <Controller
                  key={id}
                  name={SORT}
                  control={control}
                  render={({
                    field: { value },
                  }) => (
                    <TableCell head className={styles.tableCell}>
                      <TableSort
                        active={id === value.orderKey}
                        onClickSort={() => onSortChange(id === TASK_SORT_BY.NAME ? TASK_SORT_BY.TITLE : id)}
                      >
                        <span>{t(label)}</span>
                      </TableSort>
                    </TableCell>
                  )}
                />
              ) : (
                <TableCell
                  head
                  className={`${styles.tableCell} ${id === 'actions' ? styles.actionHeaderCell : ''}`}
                  key={id}
                >
                  <div>
                    <span>{t(label)}</span>
                  </div>
                </TableCell>
              )))}
            </TableHead>
            <TableBody className={styles.body}>
              {isLoading ? <Loader isPageLoading isFullLoader={false} /> : currentTaskList?.map((task) => (
                <TableRow key={[task.id, task.title].join('_')}>
                  <TableCell className={styles.nameCell}>
                    <Button
                      title={task?.title}
                      onClick={() => onGoToTask(`${task.id}`)}
                      className={styles.buttonName}
                      variant={BUTTON_VARIANT.ghost}
                    />
                  </TableCell>
                  <TableCell>{handleGetCurrentRelatedTo(task) || '-'}</TableCell>
                  <TableCell className={styles.priorityCell}>
                    {TASK_PRIORITY_OPTIONS.find((option) => option.value === task.priority)?.icon}
                    <span>
                      {TASK_PRIORITY_OPTIONS.find((option) => option.value === task.priority)?.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusTaskButton
                      taskId={task.id}
                      currentStatus={task.status as TTaskStatus}
                      onStatusChange={onStatusChange}
                      disabled={!canChangeStatus}
                    />
                  </TableCell>
                  <TableCell className={styles.actionCell}>
                    <IconButton
                      icon={<EyeIcon />}
                      onClick={() => onGoToTask(`${task.id}`)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <EmptyList text={emptyListText} />}
      </Panel>
      <DeactivationModal
        open={isCommentModalVisible}
        title={t('taskManagement.commentModalTitle')}
        text={onCheckExistStatus(pendingStatusChange?.newStatus as TTaskStatus)
          ? t('taskManagement.commentRequired') : t('taskManagement.commentOptional')}
        placeholder={t('taskManagement.commentModalDescription')}
        onReject={onCommentReject}
        onSubmit={onCommentConfirm}
        hideLabel
        isLoading={false}
        showInfoIcon
        offValidation={!onCheckExistStatus(pendingStatusChange?.newStatus as TTaskStatus)}
      />
    </div>
  );
}

export default TaskListBoard;
